import React, { useEffect, useRef, useState } from 'react';
import styles from './TextInput.module.scss';
import clsx from 'clsx';
import { ReactComponent as ClearIcon } from '@/assets/icons/actions/clear-circle.svg';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { INPUT_TYPE, InputType } from '@/constants/ui/input';
import { KEYBOARD_KEYS } from '@/constants/ui/aria';

const IME_PROCESSING_KEY_CODE = 229;

interface TextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  showCharacterCount?: boolean;
  type?: InputType;
  error?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  onEnter?: () => void;
  focusOnMount?: boolean;
  iconLead?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  showClearButton?: boolean;
  clearButtonClick?: () => void;
  clearIcon?: React.ComponentType<{
    className?: string;
    onClick?: () => void;
  }>;
  // decorative icon shown in the same trailing slot when the clear button
  // isn't - e.g. an "edit" affordance replaced by the clear button once typed.
  trailingIcon?: React.ComponentType<{ className?: string }>;
  trailingAction?: React.ReactNode;
  showPasswordToggle?: boolean;
  disabled?: boolean;
  showErrorOnMaxLength?: boolean;
  inputClassName?: string;
}

export const TextInput: React.FC<TextInputProps> = (props: TextInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange(event.target.value);
  };
  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== KEYBOARD_KEYS.ENTER) return;
    if (event.repeat || event.nativeEvent.isComposing) return;
    if (event.keyCode === IME_PROCESSING_KEY_CODE) return;
    props.onEnter?.();
  };
  useEffect(() => {
    if (props.focusOnMount) {
      inputRef.current?.focus();
    }
  }, []);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(prev => !prev);
  };

  const getInputType = (): InputType => {
    if (props.type === INPUT_TYPE.PASSWORD && props.showPasswordToggle) {
      return isPasswordVisible ? INPUT_TYPE.TEXT : INPUT_TYPE.PASSWORD;
    }
    return props.type || INPUT_TYPE.TEXT;
  };

  const isMaxLengthReached = (): boolean => {
    if (!props.showErrorOnMaxLength || !props.maxLength || props.disabled) {
      return false;
    }
    return props.value.length >= props.maxLength;
  };

  const hasError = !!props.error || isMaxLengthReached();

  const IconLead = props.iconLead;
  const ClearIconComponent = props.clearIcon ?? ClearIcon;
  const showClearButton = !!(props.showClearButton && props.value);
  const TrailingIcon = props.trailingIcon;
  return (
    <div className={styles.textInputContainer}>
      {IconLead && <IconLead className={styles.iconLead} />}
      {showClearButton && (
        <ClearIconComponent
          onClick={props.clearButtonClick}
          className={styles.clearIcon}
        />
      )}
      {!showClearButton && TrailingIcon && (
        <TrailingIcon className={styles.trailingIcon} />
      )}
      {props.trailingAction && (
        <div className={styles.trailingAction}>{props.trailingAction}</div>
      )}
      {props.type === INPUT_TYPE.PASSWORD && props.showPasswordToggle && (
        <div
          className={styles.passwordToggle}
          onClick={togglePasswordVisibility}
          data-automation="ShowPassword"
        >
          {isPasswordVisible ? (
            <VisibilityOffIcon
              className={clsx(
                styles.visibilityIcon,
                props.error && styles.iconHeightInError
              )}
            />
          ) : (
            <VisibilityIcon
              className={clsx(
                styles.visibilityIcon,
                props.error && styles.iconHeightInError
              )}
            />
          )}
        </div>
      )}
      <input
        ref={inputRef}
        className={clsx(
          styles.textInput,
          hasError && styles.error,
          props.disabled && styles.disabled,
          props.iconLead && styles.withIconLead,
          props.showPasswordToggle && styles.withPasswordToggle,
          props.trailingAction && styles.withTrailingAction,
          props.inputClassName
        )}
        type={getInputType()}
        value={props.value}
        disabled={props.disabled}
        onChange={onChangeHandler}
        placeholder={props.placeholder}
        onBlur={() => props.onBlur?.()}
        onFocus={() => props.onFocus?.()}
        onKeyDown={onKeyDownHandler}
        maxLength={props.maxLength}
      />
      {props.error && <div className={styles.errorMessage}>{props.error}</div>}
      {props.showCharacterCount && props.maxLength && (
        <div className={styles.characterCount}>
          {props.value.length}/{props.maxLength}
        </div>
      )}
    </div>
  );
};
